/**
 * File: contractDueDay.ts
 * Path: packages/config/src/contracts/contractDueDay.ts
 * Description: Which day of the month a contract's monthly payments are due.
 * Chosen per contract to suit the client: the day they signed, the 1st, the
 * 15th, the last day, or a specific day (2nd to 28th). The contract wording,
 * the payment schedule and the Money owed page all read this one choice.
 * Contracts made before it existed have none and stay on the signing day.
 */

/** "signing" | "first" | "mid" | "last" | "2" to "28" (a specific day). */
export type MonthlyDueDay = string;

export const DEFAULT_MONTHLY_DUE_DAY: MonthlyDueDay = "signing";

const NAMED = ["signing", "first", "mid", "last"] as const;

export const isMonthlyDueDay = (value: unknown): value is MonthlyDueDay => {
  if (typeof value !== "string") return false;
  if ((NAMED as readonly string[]).includes(value)) return true;
  const n = Number(value);
  return /^\d{1,2}$/.test(value) && n >= 2 && n <= 28;
};

export const toMonthlyDueDay = (value: unknown): MonthlyDueDay =>
  isMonthlyDueDay(value) ? value : DEFAULT_MONTHLY_DUE_DAY;

export const ordinal = (n: number): string => {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
  return `${n}${suffix}`;
};

/** Short label for the drop-down and the schedule. */
export const monthlyDueDayLabel = (day: MonthlyDueDay): string => {
  switch (day) {
    case "first":
      return "The 1st of the month";
    case "mid":
      return "The 15th (mid-month)";
    case "last":
      return "The last day of the month";
    case "signing":
      return "The day of the month they signed";
    default:
      return `The ${ordinal(Number(day))} of the month`;
  }
};

/** The drop-down's choices: named ones, then the 2nd to the 28th. */
export const MONTHLY_DUE_DAY_OPTIONS: ReadonlyArray<{ value: MonthlyDueDay; label: string }> = [
  { value: "signing", label: monthlyDueDayLabel("signing") },
  { value: "first", label: monthlyDueDayLabel("first") },
  { value: "mid", label: monthlyDueDayLabel("mid") },
  { value: "last", label: monthlyDueDayLabel("last") },
  ...Array.from({ length: 27 }, (_, i) => String(i + 2))
    .filter((d) => d !== "15")
    .map((d) => ({ value: d, label: monthlyDueDayLabel(d) })),
];

/** "on the 15th of each calendar month", for the contract text. */
export const monthlyDueDayPhrase = (value: MonthlyDueDay | undefined): string => {
  const day = toMonthlyDueDay(value);
  switch (day) {
    case "first":
      return "on the 1st of each calendar month";
    case "mid":
      return "on the 15th of each calendar month";
    case "last":
      return "on the last day of each calendar month";
    case "signing":
      return "on the same day of each calendar month as the Agreement was signed";
    default:
      return `on the ${ordinal(Number(day))} of each calendar month`;
  }
};

/** "15th of month 3" style text for a schedule row. */
export const monthlyDueDayRow = (value: MonthlyDueDay | undefined, month: number): string => {
  const day = toMonthlyDueDay(value);
  switch (day) {
    case "first":
      return `1st of month ${month}`;
    case "mid":
      return `15th of month ${month}`;
    case "last":
      return `Last day of month ${month}`;
    case "signing":
      return `Signing day of month ${month}`;
    default:
      return `${ordinal(Number(day))} of month ${month}`;
  }
};

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * The due date of month `n` (1 is the first month, due on signing day).
 * Month 2 onwards falls in the calendar month `n - 1` months after signing.
 */
export const monthlyDueDate = (signedOn: string, n: number, value: MonthlyDueDay | undefined): string => {
  const day = toMonthlyDueDay(value);
  const [y, m, d] = signedOn.split("-").map(Number);
  if (n <= 1) return signedOn;
  const index = y * 12 + (m - 1) + (n - 1);
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const wanted =
    day === "first" ? 1 : day === "mid" ? 15 : day === "last" ? lastDay : day === "signing" ? d : Number(day);
  return `${year}-${pad(month)}-${pad(Math.min(wanted, lastDay))}`;
};
